import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmDialogProvider } from './hooks/useConfirmDialog';
import Layout from './components/layout/Layout';
import NotFound from './components/common/NotFound';
import LoadingSpinner from './components/common/LoadingSpinner';
import ScrollToTop from './components/common/ScrollToTop';
import ConsentBanner from './components/consent/ConsentBanner';
import NotificationRegion from './components/common/NotificationRegion';
import { captureAttribution } from './utils/track';
import './utils/debugHelper';
import './App.css';

// Lazy load components for code splitting
const Login = React.lazy(() => import('./components/auth/Login'));
const Register = React.lazy(() => import('./components/auth/Register'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));
const TwoFactorVerify = React.lazy(() => import('./components/auth/TwoFactorVerify'));
const SsoCallback = React.lazy(() => import('./components/auth/SsoCallback'));
const SsoOrgRedirect = React.lazy(() => import('./components/auth/SsoOrgRedirect'));
const EmailVerification = React.lazy(() => import('./components/auth/EmailVerification'));
const ConfirmEmailChange = React.lazy(() => import('./components/auth/ConfirmEmailChange'));
const AcceptInvitation = React.lazy(() => import('./components/invitation/AcceptInvitation'));
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const PDFAnalyzer = React.lazy(() => import('./components/analyzer/PDFAnalyzer'));
const ReportViewer = React.lazy(() => import('./components/reports/ReportViewer'));
const PDFVisualization = React.lazy(() => import('./components/visualization/PDFVisualization'));
const JobStatus = React.lazy(() => import('./components/jobs/JobStatus'));
const History = React.lazy(() => import('./components/history/History'));
const Logs = React.lazy(() => import('./components/logs/Logs'));
const DiscoverPage = React.lazy(() => import('./components/discover/DiscoverPage'));
const DiscoveryJobDetail = React.lazy(() => import('./components/discover/DiscoveryJobDetail'));
const BillingDashboard = React.lazy(() => import('./components/billing-v2/BillingDashboard'));
const ProfessionalSignup = React.lazy(() => import('./components/billing-v2/ProfessionalSignup'));
const EnterpriseRequestForm = React.lazy(
  () => import('./components/billing-v2/EnterpriseRequestForm')
);
const AccountSettings = React.lazy(() => import('./components/account/AccountSettings'));
const OrganizationSettings = React.lazy(
  () => import('./components/organization/OrganizationSettings')
);
const EnterpriseAuth = React.lazy(() => import('./components/organization/auth/EnterpriseAuth'));
const SsoSetupWizard = React.lazy(() => import('./components/organization/auth/SsoSetupWizard'));
const ConvertPage = React.lazy(() => import('./components/convert/ConvertPage'));
const RemediationPage = React.lazy(() => import('./components/remediation/RemediationPage'));
const RemediationDetail = React.lazy(() => import('./components/remediation/RemediationDetail'));
const DevelopersPage = React.lazy(() => import('./components/developers/DevelopersPage'));
const PublicChrome = React.lazy(() => import('./components/landing/PublicChrome'));
const HowToPage = React.lazy(() => import('./components/howto/HowToPage'));
const HowToDetail = React.lazy(() => import('./components/howto/HowToDetail'));
const AdminRoute = React.lazy(() => import('./components/admin/AdminRoute'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const AdminUserList = React.lazy(() => import('./components/admin/AdminUserList'));
const AdminUserDetail = React.lazy(() => import('./components/admin/AdminUserDetail'));
const AdminRemediationQueue = React.lazy(() => import('./components/admin/AdminRemediationQueue'));
const AdminRemediationDetail = React.lazy(
  () => import('./components/admin/AdminRemediationDetail')
);
const AdminQueueLogs = React.lazy(() => import('./components/admin/AdminQueueLogs'));
const WebhooksPage = React.lazy(() => import('./components/webhooks/WebhooksPage'));
const WebhookDeliveries = React.lazy(() => import('./components/webhooks/WebhookDeliveries'));
const ComplianceIndex = React.lazy(() => import('./components/legal/ComplianceIndex'));
const PolicyPage = React.lazy(() => import('./components/legal/PolicyPage'));
const GDPRConsent = React.lazy(() => import('./components/gdpr/GDPRConsent'));
const LandingPage = React.lazy(() => import('./components/landing/LandingPage'));
const DocumentAccessibilityLanding = React.lazy(
  () => import('./components/landing/DocumentAccessibilityLanding')
);
const PublicReport = React.lazy(() => import('./components/landing/PublicReport'));
const ContactPage = React.lazy(() => import('./components/contact/ContactPage'));

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>;
};

const EmailVerificationRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? (
    <Layout>
      <EmailVerification />
    </Layout>
  ) : (
    <EmailVerification />
  );
};

const OptionalLayoutRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Layout>{children}</Layout> : <>{children}</>;
};

// Like OptionalLayoutRoute, but logged-out visitors get the public marketing
// chrome (header/footer) instead of a bare page. Used for public secondary
// pages such as the API docs so they match the unauthenticated homepage (#700).
const PublicChromeRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Layout>{children}</Layout> : <PublicChrome>{children}</PublicChrome>;
};

const LandingRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route path="/verify-2fa" element={<TwoFactorVerify />} />
        {/* SSO entry/exit routes (#218). Public and NOT wrapped in PublicRoute:
            the callback must run even for an authenticated session so it can
            establish the new SSO session from the fragment token. */}
        <Route path="/auth/sso/callback" element={<SsoCallback />} />
        <Route path="/login/sso/:organizationSlug" element={<SsoOrgRedirect />} />
        <Route path="/verify-email" element={<EmailVerificationRoute />} />
        {/* The link mailed to a new address (OA-145). Public: it may be opened
            in a browser that is not signed in. */}
        <Route
          path="/confirm-email-change"
          element={
            <OptionalLayoutRoute>
              <ConfirmEmailChange />
            </OptionalLayoutRoute>
          }
        />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/analyze"
          element={
            <PrivateRoute>
              <Layout>
                <PDFAnalyzer />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports/:reportId"
          element={
            <PrivateRoute>
              <Layout>
                <ReportViewer />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports/:reportId/visualize"
          element={
            <PrivateRoute>
              <PDFVisualization />
            </PrivateRoute>
          }
        />
        <Route
          path="/jobs/:jobId"
          element={
            <PrivateRoute>
              <Layout>
                <JobStatus />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/history"
          element={
            <PrivateRoute>
              <Layout>
                <History />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/discover"
          element={
            <PrivateRoute>
              <Layout>
                <DiscoverPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/discover/:jobId"
          element={
            <PrivateRoute>
              <Layout>
                <DiscoveryJobDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/logs"
          element={
            <PrivateRoute>
              <Layout>
                <Logs />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/developers"
          element={
            // Public docs: readable without login (#603). Authenticated users
            // get the full app shell; logged-out visitors get the public
            // marketing header/footer so the page matches the unauthenticated
            // homepage instead of rendering bare (#700).
            <PublicChromeRoute>
              <DevelopersPage />
            </PublicChromeRoute>
          }
        />
        <Route
          path="/webhooks"
          element={
            <PrivateRoute>
              <Layout>
                <WebhooksPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/webhooks/deliveries"
          element={
            <PrivateRoute>
              <Layout>
                <WebhookDeliveries />
              </Layout>
            </PrivateRoute>
          }
        />
        {/*
          Public: the how-to guides are marketing content, so logged-out
          visitors get them with the same chrome as the unauthenticated
          homepage, while signed-in users keep the app Layout they navigated
          from. Role-gated topics stay gated -- getTopicBySlug() drops any
          topic with a requiredRole when there is no qualifying role, so the
          admin guides remain unreachable without one.
        */}
        <Route
          path="/how-to"
          element={
            <PublicChromeRoute>
              <HowToPage />
            </PublicChromeRoute>
          }
        />
        <Route
          path="/how-to/:slug"
          element={
            <PublicChromeRoute>
              <HowToDetail />
            </PublicChromeRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <PrivateRoute>
              <Layout>
                <BillingDashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing/professional"
          element={
            <PrivateRoute>
              <Layout>
                <ProfessionalSignup />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing/enterprise"
          element={
            <PrivateRoute>
              <Layout>
                <EnterpriseRequestForm />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <Layout>
                <AccountSettings />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <PrivateRoute>
              <Layout>
                <OrganizationSettings />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/organization/authentication"
          element={
            <PrivateRoute>
              <Layout>
                <EnterpriseAuth />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/organization/authentication/setup"
          element={
            <PrivateRoute>
              <Layout>
                <SsoSetupWizard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route path="/pricing" element={<Navigate to="/billing" replace />} />
        <Route
          path="/convert"
          element={
            <PrivateRoute>
              <Layout>
                <ConvertPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/remediation"
          element={
            <PrivateRoute>
              <Layout>
                <RemediationPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/remediation/:requestId"
          element={
            <PrivateRoute>
              <Layout>
                <RemediationDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminUserList />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminUserDetail />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/queue"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminQueueLogs />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/remediation"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminRemediationQueue />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/remediation/:requestId"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <AdminRemediationDetail />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        {/* The source report of a remediation request, read through the admin
            routes so any organisation's report opens (OA-164). */}
        <Route
          path="/admin/reports/:reportId"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Layout>
                  <AdminLayout>
                    <ReportViewer admin />
                  </AdminLayout>
                </Layout>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/reports/:reportId/visualize"
          element={
            <PrivateRoute>
              <AdminRoute>
                <PDFVisualization admin />
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/legal"
          element={
            <OptionalLayoutRoute>
              <ComplianceIndex />
            </OptionalLayoutRoute>
          }
        />
        <Route
          path="/legal/:slug"
          element={
            <OptionalLayoutRoute>
              <PolicyPage />
            </OptionalLayoutRoute>
          }
        />
        {/* Cookie preferences (#322).
            Deliberately NOT wrapped in PublicRoute or PrivateRoute: the whole
            point is that an anonymous visitor can review and change a decision
            they made from the banner, and an authenticated one can reach the
            same page. Before this route the preference centre existed only
            inside AccountSettings, which is behind auth — so the majority of
            people who ever see a cookie banner had no way back to it. */}
        <Route
          path="/cookie-preferences"
          element={
            <PublicChromeRoute>
              <GDPRConsent />
            </PublicChromeRoute>
          }
        />
        <Route path="/public-report/:token" element={<PublicReport />} />
        {/* Paid-search landing page. `noindex` (set by the page), and deliberately absent from any
            sitemap. PublicChrome directly rather than PublicChromeRoute: signed-in visitors get the
            same campaign page, not the app shell. Must stay above the catch-all. */}
        <Route
          path="/lp/document-accessibility"
          element={
            <PublicChrome campaign>
              <DocumentAccessibilityLanding />
            </PublicChrome>
          }
        />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/" element={<LandingRoute />} />
        {/* Catch-all. Must stay last: React Router picks the best match, but
            keeping it here makes the ordering intent obvious. Deliberately not
            wrapped in PrivateRoute -- an unknown URL should say so, not bounce
            an anonymous visitor to /login as though the page existed (#820). */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Once, at the first render: the landing URL is the one carrying UTM parameters and gclid.
  useEffect(() => {
    captureAttribution();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <ConfirmDialogProvider>
          <div className="App">
            <AppRoutes />
            {/* Owns the initial consent lookup as well as the prompt. This replaced a
                `useAnalyticsConsent` hook that ran here and did the same lookup: two of them race,
                and a late-resolving one can call syncAnalyticsConsent(false) moments after the
                visitor presses Accept, silently undoing the choice. Inside <Router> because the
                banner suppresses itself by route. */}
            <ConsentBanner />
            {/* Job and discovery notifications (OA-220). Inside <Router>: it links to the job
                and stays quiet on that job's own page. Renders nothing when signed out, or until
                the realtime client registers as its transport. */}
            <NotificationRegion />
          </div>
        </ConfirmDialogProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
